import { supabase } from "./supabase";

export type ShortlistEntry = {
  id: string;
  competition_id: string;
  client_user_id: string;
  student_id: string;
  notes: string | null;
  created_at: string;
};

export type ShortlistRow = ShortlistEntry & {
  student_full_name: string | null;
  student_email: string | null;
  student_university: string | null;
  student_major: string | null;
  student_graduation_year: number | null;
  student_avatar_url: string | null;
  /** Number of submissions made by this student's team in the competition. */
  submission_count: number;
  final_score: number | null;
  placement: number | null;
  eliminated_at_stage: number | null;
};

type TeamMemberLite = { student_id: string; team_id: string | null };
type CompetitionResultLite = {
  team_id: string;
  final_score: number | null;
  placement: number | null;
  eliminated_at_stage: number | null;
};

export async function listMyShortlist(competitionId: string): Promise<ShortlistRow[]> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return [];

  const { data: entries, error } = await supabase
    .from("shortlist_entries")
    .select("*")
    .eq("competition_id", competitionId)
    .eq("client_user_id", userData.user.id)
    .order("created_at", { ascending: false });

  if (error || !entries?.length) return [];

  const typedEntries = entries as ShortlistEntry[];
  const studentIds = typedEntries.map((e) => e.student_id);

  const [{ data: profiles }, { data: students }, { data: memberships }] = await Promise.all([
    supabase.from("user_profiles").select("id, full_name, email, avatar_url").in("id", studentIds),
    supabase.from("students").select("id, university, major, graduation_year").in("id", studentIds),
    supabase
      .from("team_members")
      .select("student_id, team_id")
      .eq("competition_id", competitionId)
      .in("student_id", studentIds),
  ]);

  const profileById = new Map((profiles ?? []).map((p) => [String(p.id), p]));
  const studentsById = new Map((students ?? []).map((s) => [String(s.id), s]));
  const teamByStudent = new Map(
    ((memberships ?? []) as TeamMemberLite[]).map((r) => [r.student_id, r.team_id])
  );

  const teamIds = Array.from(new Set((memberships ?? []).map((r) => (r as TeamMemberLite).team_id).filter(Boolean))) as string[];
  const [{ data: cr }, { data: subs }] =
    teamIds.length > 0
      ? await Promise.all([
          supabase
            .from("competition_results")
            .select("team_id, final_score, placement, eliminated_at_stage")
            .eq("competition_id", competitionId)
            .in("team_id", teamIds),
          supabase
            .from("submissions")
            .select("team_id")
            .eq("competition_id", competitionId)
            .in("team_id", teamIds),
        ])
      : [
          { data: [] as CompetitionResultLite[] },
          { data: [] as { team_id: string }[] },
        ];

  const resultsByTeam = new Map(((cr ?? []) as CompetitionResultLite[]).map((r) => [r.team_id, r]));
  const subCountByTeam = new Map<string, number>();
  for (const s of (subs ?? []) as { team_id: string }[]) {
    subCountByTeam.set(s.team_id, (subCountByTeam.get(s.team_id) ?? 0) + 1);
  }

  return typedEntries.map((e) => {
    const profile = profileById.get(e.student_id) as
      | { full_name?: string | null; email?: string | null; avatar_url?: string | null }
      | undefined;
    const student = studentsById.get(e.student_id) as
      | { university?: string | null; major?: string | null; graduation_year?: number | null }
      | undefined;
    const teamId = teamByStudent.get(e.student_id);
    const result = teamId ? resultsByTeam.get(teamId) : null;
    const submissionCount = teamId ? subCountByTeam.get(teamId) ?? 0 : 0;
    return {
      ...e,
      student_full_name: profile?.full_name ?? null,
      student_email: profile?.email ?? null,
      student_university: student?.university ?? null,
      student_major: student?.major ?? null,
      student_graduation_year: student?.graduation_year ?? null,
      student_avatar_url: profile?.avatar_url ?? null,
      submission_count: submissionCount,
      final_score: result?.final_score ?? null,
      placement: result?.placement ?? null,
      eliminated_at_stage: result?.eliminated_at_stage ?? null,
    };
  });
}

export async function isShortlisted(
  competitionId: string,
  studentId: string
): Promise<{ id: string; notes: string | null } | null> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return null;
  const { data } = await supabase
    .from("shortlist_entries")
    .select("id, notes")
    .eq("competition_id", competitionId)
    .eq("client_user_id", userData.user.id)
    .eq("student_id", studentId)
    .maybeSingle();
  if (!data) return null;
  return { id: String(data.id), notes: (data.notes as string | null | undefined) ?? null };
}

export async function addToShortlist(input: {
  competitionId: string;
  studentId: string;
  notes?: string;
}): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error("Not signed in");
  const { error } = await supabase.from("shortlist_entries").insert({
    competition_id: input.competitionId,
    client_user_id: userData.user.id,
    student_id: input.studentId,
    notes: input.notes?.trim() ? input.notes.trim() : null,
  });
  if (error) throw new Error(error.message);
}

export async function updateShortlistNotes(id: string, notes: string | null): Promise<void> {
  const { error } = await supabase
    .from("shortlist_entries")
    .update({ notes: notes?.trim() ? notes.trim() : null })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

export async function removeFromShortlist(id: string): Promise<void> {
  const { error } = await supabase.from("shortlist_entries").delete().eq("id", id);
  if (error) throw new Error(error.message);
}
